'use strict';
// @ts-check
/* global module */

declare var $;

import { ICSCartJsonResponse } from '../tv_shipping/ICSCartJsonResponse';
import { Notifications } from '../tv_utils/notifications';
import { config } from '../tv_utils/global_config_data.js';

/**
 * Handles email validation.
 */
export class EmailValidation {

    /**
     * @var {string[]} domains_blacklist is an array of strings containing potentially misspelled domain names.
     * @var {HTMLInputElement} email is the input that validation will be applied to.
     * @var {HTMLElement} email_is_registerd is the message indicating the email has been registered with
     *  the language variable tv_users_email_input_email_registered.
     * @var {Function|null} validation_callback is for custom callbacks from checking if the email is registered.
     * @var {boolean} registration_check is the flag for whether we want to check if the email is registrated.
     */
    domains_blacklist: string[];
    email: HTMLInputElement;
    email_is_registerd: HTMLElement;
    validation_callback: Function|null;
    registration_check: boolean;

    /**
     * Instantiate the class by adding an event listener to the associated email input.
     *
     * @param {HTMLInputElement} email is the input element for validation.
     * @param {Function|null} validation_callback is for custom callbacks from checking if the email is registered.
     */
    constructor(
        email: HTMLInputElement,
        validation_callback: Function|null = null,
        registration_check: boolean = false
    ) {
        if (email) {
            this.email = email;
            this.email_is_registerd = document.getElementById('js-tv-email-is-registered');
            this.validation_callback = validation_callback;
            this.registration_check = registration_check;

            // Enter button will validate email instead of submitting form.
            this.email.addEventListener('keydown', event => {
                if (event.keyCode === 13) {
                    event.preventDefault();
                    this.email.blur();
                    return false;
                }
            });


            this.email.addEventListener('change', this.validate.bind(this));
            this.domains_blacklist = config().email_verification_blacklist;

            // If the email has already been auto-populated by the browser we want to run
            // the validation logic right away.
            const email_value = this.email.value.trim();
            if (email_value) {
                this.validate();
            }
        }
    }

    /**
     * Validates the email address first by HTML Input type validation then
     * by custom validation for top-level domain checks.
     *
     * @returns {void}
     */
    validate() {
        const email = this.email.value.trim();

        // Only continue if an email was entered and this element is visible.
        if (email && this.email.offsetParent) {
            // First, check by HTML Input type validation.
            // This will set the email to the name@domain.com format.
            if (!this.email.validity.valid) {
                this.email.reportValidity();
                this.email.focus();
                return;
            }

            // Second, check if the top-level domain of the email address is in
            // our commonly mistaken domains blacklist.
            const email_pieces = this.email.value.split('.').reverse();

            // If the entered email address does not match our list of domains,
            // send an alert to the user but allow them to continue.
            if (this.domains_blacklist.includes(email_pieces[0]) || this.domains_blacklist.includes(email_pieces[1])) {
                this.email.focus();
                // Set the cscart notification.
                Notifications.set_notification(
                    'W',
                    'E-mail Warning',
                    `There might be a problem with your email address. Please verify your email address is correct.`
                );
            } else { // Otherwise the email is good and we can check if it's been registered before.
                // Don't want to do this for Login Recovery.
                if (!this.email.parentElement.nextElementSibling?.classList?.contains('login-recovery') &&
                    this.registration_check
                ) {
                    this.check_email_for_registration(email);
                }
            }
        }
    }

    /**
     * Check to see if the email has already been registered for a webstore account.
     *
     * @param {string} email is the email address entered.
     * @return {void}
     */
    private check_email_for_registration(email: string) : void {
        $.ceAjax('request', 'index.php?dispatch=tv_users.check_email_for_registration', {
            'method': 'POST',
            'caching': false,
            'data': {
                email: email
            },
            'message': 'Checking Email',
            'callback': response => {
                if (this.validation_callback) {
                    this.validation_callback(email, response);
                } else {
                    this.handle_check_email_for_registration(email, response);
                }
            }
        });
    }

    /**
     * Handle the response from the check for if the email was registered for a webstore account.
     *
     * @param {string} email The email addressed that was checked.
     * @param {ICSCartJsonResponse} response from the native AJAX.
     * @return {void}
     */
    private handle_check_email_for_registration(email: string, response: ICSCartJsonResponse) : void {
        // If the email is registered, notify the user by displaying a notification and
        // a message with links to login or reset their password. Disable the fields too
        // so they don't try to finish filling out the form.
        if (response.is_registered) {
            // Set the cscart notification.
            Notifications.set_notification(
                'E',
                'Account Exists',
                `The provided email: ${email} is already associated with an account in our system.`
            );

            // Display the message under the email input notifying the user of the registered email.
            if (this.email_is_registerd) {
                this.email_is_registerd.style.display = 'block';
            }

            this.email.focus();

            return;
        }

        // Hide the message and enable the fields if the email is good to register.
        if (this.email_is_registerd) {
            this.email_is_registerd.style.display = 'none';
        }
    }

}

export function init() {
    // Fetch all email inputs on the page for validation.
    const email_inputs = document.querySelectorAll('[name*="email"]');
    // Query Selector All returns an array like list of elements which could contain no elements.
    if (email_inputs.length > 0) {
        for (const input of email_inputs) {
            const email_input = <HTMLInputElement> input;
            // Registration is the only time we want to check if the email is already registered.
            if (email_input.classList.contains('js-tv-email-registration')) {
                (() => new EmailValidation(email_input, null, true))();
            }
        }
    }
}
